import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../../t';
import { useParams } from 'react-router-dom';
import withAuth from '../../../HOC/withAuth';
import { copyImageToClipboard, getShareInfo } from '../../../util';
import StepShell from '../StepShell';
import C from '../../../colors';
import dayjs from 'dayjs';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

function getCountdown(expiredTime, now) {
  const remainingMs = Math.max(0, dayjs(expiredTime).valueOf() - now);
  const days = Math.floor(remainingMs / DAY_MS);
  const hours = Math.floor((remainingMs % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((remainingMs % HOUR_MS) / MINUTE_MS);
  const seconds = Math.floor((remainingMs % MINUTE_MS) / 1000);
  const pad = value => String(value).padStart(2, '0');
  return {
    remainingSeconds: Math.ceil(remainingMs / 1000),
    text: remainingMs > 0
      ? `${days} 天 ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : '已过期',
  };
}

const Step4 = inject('snackbarStore', 'ordersStore')(observer(({ snackbarStore, ordersStore }) => {
  const { orderId } = useParams();
  const [expire, setExpire] = useState(7);
  const [expiredDays, setExpiredDays] = useState(0);
  const [isShared, setIsShared] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordType, setPasswordType] = useState('random');
  const [passwordError, setPasswordError] = useState(false);
  const [miniprogrameQrCodeUrl, setMiniprogrameQrCodeUrl] = useState('');
  const [autoFill, setAutoFill] = useState(false);
  const [url, setUrl] = useState('');
  const [loadingQR, setLoadingQR] = useState(true);
  const [copied, setCopied] = useState(null);
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [expiryError, setExpiryError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [countdownNow, setCountdownNow] = useState(Date.now());
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const { t } = useTranslation();

  const generateCode = () => {
    let code = String(Math.floor(Math.random() * 10000));
    return code.padStart(4, '0');
  };

  const passwordRegex = /^[A-Za-z0-9]{4}$/;
  const validatePassword = (p) => {
    if (!p || !passwordRegex.test(p)) return t('Code must be 4 numbers');
    return '';
  };

  const getQRCode = useCallback(() => {
    setLoadingQR(true);
    ordersStore.fetchQRCode(orderId, autoFill ? password : undefined)
      .then(imageData => {
        setMiniprogrameQrCodeUrl(imageData);
        setLoadingQR(false);
      }).catch(() => {
        snackbarStore.showSnackbar(t('Failed to get QR code'), 'error');
        setLoadingQR(false);
      });
  }, [orderId, autoFill, password, ordersStore, snackbarStore, t]);

  const saveInfo = useCallback(() => {
    const expiryDays = Number(expire);
    if (!Number.isInteger(expiryDays) || expiryDays < 1 || expiryDays > 3650) {
      setExpiryError('请输入 1–3650 之间的整数天数');
      return;
    }
    const newPassword = passwordType === 'random' ? generateCode() : password;
    if (validatePassword(newPassword)) {
      setPasswordError(true);
      return;
    }
    setExpiryError('');
    ordersStore.setShare(orderId, newPassword, expiryDays).then(() => {
      ordersStore.fetchOrder(orderId);
      getQRCode();
      snackbarStore.showSnackbar(t('Save Share'));
    });
  }, [orderId, expire, password, passwordType, ordersStore, snackbarStore, t, getQRCode]);

  const cancelShare = () => {
    if (Number(ordersStore.currentOrder?.selectionNum || 0) > 0) return;
    setOrderLoaded(false);
    ordersStore.cancelShare(orderId).then(() => {
      return ordersStore.fetchOrder(orderId);
    }).catch(() => {}).finally(() => setOrderLoaded(true));
  };

  const setShareExpiry = async () => {
    const days = Number(expire);
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      setExpiryError('请输入 1–3650 之间的整数天数');
      return;
    }

    setSavingExpiry(true);
    setExpiryError('');
    try {
      await ordersStore.setShareExpiryDays(orderId, days);
      await ordersStore.fetchOrder(orderId);
      snackbarStore.showSnackbar(`分享有效期已设置为从现在起 ${days} 天`);
    } catch {
      // The shared HTTP interceptor shows the server error.
    } finally {
      setSavingExpiry(false);
    }
  };

  const changePassword = async () => {
    const error = validatePassword(newPassword);
    if (error) {
      setNewPasswordError(error);
      return;
    }
    if (newPassword === password) {
      setNewPasswordError('新提取码不能与当前提取码相同');
      return;
    }
    if (!window.confirm('修改后，旧提取码和客户当前登录状态将立即失效。已有选片结果不会受到影响。确定继续吗？')) return;

    setChangingPassword(true);
    try {
      await ordersStore.changeSharePassword(orderId, newPassword);
      await ordersStore.fetchOrder(orderId);
      setNewPasswordError('');
      setAutoFill(false);
      snackbarStore.showSnackbar('提取码已修改，旧客户会话已失效');
    } catch {
      // The shared HTTP interceptor shows the server error.
    } finally {
      setChangingPassword(false);
    }
  };

  const randomizePassword = () => {
    let code = generateCode();
    if (code === password) {
      code = String((Number(code) + 1) % 10000).padStart(4, '0');
    }
    setNewPassword(code);
    setNewPasswordError('');
  };

  const copyToClipboard = async () => {
    try {
      const text = !autoFill ? `${t('Link')}: ${url}  ${t('Code')}: ${password}` : url;
      await navigator.clipboard.writeText(text);
      setCopied('link');
      snackbarStore.showSnackbar(t('Text copied to clipboard'));
      setTimeout(() => setCopied(null), 1500);
    } catch {
      snackbarStore.showSnackbar(t('Failed to copy text.'), 'error');
    }
  };

  useEffect(() => {
    if (!ordersStore.currentOrder) return;
    const o = ordersStore.currentOrder;
    const pwd = o.Album?.password || '';
    if (pwd.length > 0) {
      setIsShared(true);
      setPassword(pwd);
      setNewPassword(pwd);
      setNewPasswordError('');
      setExpire(o.Album?.expire);
      setExpiredDays(o.Album?.expiredTime);
    } else {
      setIsShared(false);
      setPassword('');
      setNewPassword('');
      setNewPasswordError('');
      setExpire(7);
      setExpiredDays(0);
    }
  }, [ordersStore.currentOrder]);

  useEffect(() => {
    if (!isShared || !expiredDays) return undefined;
    setCountdownNow(Date.now());
    const timer = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isShared, expiredDays]);

  useEffect(() => {
    let active = true;
    setOrderLoaded(false);
    setLoadFailed(false);
    ordersStore.fetchOrder(orderId).then(() => {
      if (!active) return;
      if (String(ordersStore.currentOrder?.id) !== String(orderId)) {
        setLoadFailed(true);
        return;
      }
      setOrderLoaded(true);
    });
    ordersStore.fetchQRCode(orderId).then(imageData => {
      setMiniprogrameQrCodeUrl(imageData);
    });
    return () => { active = false; };
  }, [orderId]);

  useEffect(() => {
    if (password.length === 4) {
      const base = `${window.location.origin}/customer/order/${orderId}/select`;
      setUrl(autoFill ? `${base}?pwd=${password}` : base);
      getQRCode();
    }
  }, [autoFill, password, orderId]);

  const order = ordersStore.currentOrder || {};
  const hasSelections = Number(order.selectionNum || 0) > 0;
  const countdown = getCountdown(expiredDays, countdownNow);

  if (loadFailed) return <div style={{ padding: 40, color: C.ink3 }}>订单加载失败，请刷新重试</div>;
  if (!orderLoaded) return <div style={{ padding: 40, color: C.ink3 }}>Loading...</div>;

  return (
    <StepShell order={order}>
      <style>{`
        @keyframes shareCountdownPulse {
          0%, 100% { opacity: .35; transform: scale(.85); }
          50% { opacity: 1; transform: scale(1); }
        }
        .share-countdown-dot { animation: shareCountdownPulse 1.4s ease-in-out infinite; }
      `}</style>
      {!isShared ? (
        // ── Not shared: setup form ──
        <div style={{ padding: '40px 44px 64px', maxWidth: 640, flex: 1 }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 4 }}>{t('Expire')}</div>
            <div style={{ fontSize: 12.5, color: C.ink3, lineHeight: 1.5, marginBottom: 10 }}>
              超过有效期后链接自动失效，可在此页重新发布。
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                data-testid="share-expire-input"
                type="number"
                min="1"
                max="3650"
                step="1"
                value={expire}
                onChange={event => {
                  setExpire(event.target.value);
                  setExpiryError('');
                }}
                aria-label="分享有效天数"
                style={{
                  width: 88, padding: '8px 10px', background: '#fff',
                  border: `1.5px solid ${expiryError ? '#e88c7c' : C.hair2}`, borderRadius: 7,
                  fontFamily: C.mono, fontSize: 14, color: C.ink, outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: C.ink2 }}>天</span>
              <div style={{ display: 'inline-flex', padding: 3, background: C.paper2, border: `1px solid ${C.hair}`, borderRadius: 7 }}>
              {[{ v: '7', l: t('7 days') }, { v: '14', l: t('14 days') }, { v: '30', l: t('30 days') }].map(o => (
                <button key={o.v} type="button" data-testid={`share-expire-${o.v}`} aria-pressed={Number(expire) === Number(o.v)} onClick={() => setExpire(Number(o.v))} style={{
                  padding: '6px 16px', border: 'none',
                  background: Number(expire) === Number(o.v) ? '#fff' : 'transparent',
                  color: Number(expire) === Number(o.v) ? C.ink : C.ink3,
                  fontFamily: C.sans, fontSize: 13, fontWeight: 500,
                  borderRadius: 5, cursor: 'pointer',
                  boxShadow: Number(expire) === Number(o.v) ? '0 1px 3px rgba(0,0,0,.06), inset 0 0 0 1px var(--hair)' : 'none',
                }}>{o.l}</button>
              ))}
              </div>
            </div>
            {expiryError && <div data-testid="share-create-expiry-error" style={{ marginTop: 6, fontSize: 12, color: '#c2412e' }}>{expiryError}</div>}
          </div>

          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 4 }}>{t('Code')}</div>
            <div style={{ fontSize: 12.5, color: C.ink3, lineHeight: 1.5, marginBottom: 10 }}>
              客户需要输入 4 位密码才能查看相册。
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 14px',
                border: `1px solid ${passwordType === 'random' ? C.brand : C.hair2}`,
                borderRadius: 8, cursor: 'pointer',
                background: passwordType === 'random' ? 'rgba(247,119,176,.04)' : '#fff',
              }}>
                <input type="radio" checked={passwordType === 'random'} onChange={() => setPasswordType('random')} style={{ accentColor: C.brand, marginTop: 2 }}/>
                <div>
                  <div style={{ fontSize: 14, color: C.ink }}>{t('random')}</div>
                  <div style={{ fontSize: 12, color: C.ink3, marginTop: 2 }}>系统自动生成 4 位数字密码</div>
                </div>
              </label>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 14px',
                border: `1px solid ${passwordType === 'manul' ? C.brand : C.hair2}`,
                borderRadius: 8, cursor: 'pointer',
                background: passwordType === 'manul' ? 'rgba(247,119,176,.04)' : '#fff',
              }}>
                <input type="radio" checked={passwordType === 'manul'} onChange={() => setPasswordType('manul')} style={{ accentColor: C.brand, marginTop: 2 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: C.ink, marginBottom: 6 }}>自定义</div>
                  <input
                    data-testid="share-custom-password"
                    type="text" maxLength={4}
                    value={password}
                    disabled={passwordType !== 'manul'}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (v.length <= 4) {
                        setPassword(v);
                        setPasswordError(validatePassword(v));
                      }
                    }}
                    placeholder="如：1234"
                    style={{
                      width: 120, padding: '8px 12px',
                      border: `1px solid ${passwordError ? '#e88c7c' : C.hair2}`, borderRadius: 6,
                      fontFamily: C.mono, fontSize: 16, letterSpacing: 4, textAlign: 'center',
                      outline: 'none',
                      background: passwordType === 'manul' ? '#fff' : C.paper2,
                    }}
                  />
                  {passwordError && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>{passwordError}</div>}
                </div>
              </label>
            </div>
          </div>

          <button type="button" data-testid="share-save" onClick={saveInfo} style={{
            fontFamily: C.sans, fontSize: 14, fontWeight: 500,
            padding: '10px 18px', borderRadius: 7,
            background: C.brand, color: '#fff', border: 'none',
            cursor: 'pointer',
            boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
          }}>{t('Save Share')}</button>
        </div>
      ) : (
        // ── Shared: link & QR display ──
        <div style={{ padding: '36px 44px 64px', flex: 1, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            {/* Web Link card */}
            <div style={{
              background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: 28,
            }}>
              <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.brand, fontWeight: 600, marginBottom: 14 }}>
                Web link · 网页链接
              </div>
              <div style={{ fontFamily: C.serif, fontSize: 22, color: C.ink, marginBottom: 24 }}>
                客户可在任何浏览器打开
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, color: C.ink3, marginBottom: 6 }}>{t('Shared Link')}</div>
                <div style={{
                  padding: '12px 14px', border: `1px solid ${C.hair2}`, borderRadius: 6,
                  background: C.paper2, fontFamily: C.mono, fontSize: 12, color: C.ink, wordBreak: 'break-all',
                }} data-testid="share-url">{url}</div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: C.ink3, marginBottom: 6 }}>{t('Code')}</div>
                <div data-testid="share-password" style={{ display: 'inline-flex', gap: 6 }}>
                  {password.split('').map((d, i) => (
                    <div key={i} style={{
                      width: 38, height: 46, display: 'grid', placeItems: 'center',
                      border: `1px solid ${C.hair2}`, borderRadius: 6,
                      background: '#fff', fontFamily: C.serif, fontSize: 24, color: C.ink,
                    }}>{d}</div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 13, color: C.ink }}>{t('Auto fill password')}</span>
                <button type="button" data-testid="share-auto-fill-toggle" aria-label={t('Auto fill password')} aria-pressed={autoFill} onClick={() => setAutoFill(!autoFill)} style={{
                  width: 44, height: 24, borderRadius: 12,
                  border: 'none', padding: 2,
                  background: autoFill ? C.brand : C.hair2,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                  justifyContent: autoFill ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}/>
                </button>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button type="button" data-testid="share-copy-link" onClick={copyToClipboard} style={{
                  fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                  padding: '10px 18px', borderRadius: 7,
                  background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
                  boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
                }}>
                  {copied === 'link' ? '✓ 已复制' : '⧉ ' + t('Copy url and password to clipboard')}
                </button>
              </div>

              <div data-testid="share-expiry-status" style={{
                padding: '13px 14px', borderRadius: 9,
                background: C.paper2, border: `1px solid ${C.hair}`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, marginBottom: 9, flexWrap: 'wrap',
                }}>
                  <span data-testid="share-remaining-days" style={{ fontSize: 13, color: C.ink2 }}>
                    {getShareInfo(expiredDays)}
                  </span>
                  <span
                    data-testid="share-expiry-date"
                    data-expired-time={expiredDays ? dayjs(expiredDays).toISOString() : ''}
                    style={{ fontSize: 12, color: C.ink3 }}
                  >
                    有效至 {expiredDays ? dayjs(expiredDays).format('YYYY-MM-DD HH:mm:ss') : '—'}
                  </span>
                </div>
                <div
                  data-testid="share-expiry-countdown"
                  data-remaining-seconds={countdown.remainingSeconds}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, flexWrap: 'nowrap',
                    fontFamily: C.mono, fontSize: 15,
                    color: countdown.remainingSeconds > 0 ? C.ink : C.ink3,
                    fontVariantNumeric: 'tabular-nums', letterSpacing: .3,
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {countdown.remainingSeconds > 0 ? countdown.text : '已过期'}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: C.sans, fontSize: 11,
                    color: countdown.remainingSeconds > 0 ? C.brand2 : C.ink3,
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    <span className={countdown.remainingSeconds > 0 ? 'share-countdown-dot' : ''} style={{
                      width: 6, height: 6, borderRadius: 3,
                      background: countdown.remainingSeconds > 0 ? C.brand : C.hair2,
                    }}/>
                    {countdown.remainingSeconds > 0 ? '倒计时' : '已结束'}
                  </span>
                </div>
              </div>
            </div>

            {/* Mini Program card */}
            <div style={{
              background: `linear-gradient(155deg, ${C.ink} 0%, #2a2420 100%)`,
              color: '#f4ede1', borderRadius: 12, padding: 28,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: -60, right: -60,
                width: 240, height: 240, borderRadius: 120,
                background: `radial-gradient(circle, ${C.brand}4d, transparent 70%)`,
                filter: 'blur(20px)',
              }}/>
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.brand, fontWeight: 600, marginBottom: 14 }}>
                  WeChat mini · 小程序
                </div>
                <div style={{ fontFamily: C.serif, fontSize: 22, marginBottom: 24, color: '#fff' }}>
                  让客户用微信扫码即看
                </div>

                <div style={{
                  display: 'flex', alignItems: 'center', gap: 24,
                  padding: 20, background: 'rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, marginBottom: 22,
                }}>
                  <div style={{ width: 140, height: 140, background: '#fff', borderRadius: 8, padding: 8, flexShrink: 0 }}>
                    {loadingQR ? (
                      <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}><div style={{width:20,height:20,border:"2px solid #fff",borderTopColor:"transparent",borderRadius:"50%",animation:"spin .8s linear infinite"}} /></div>
                    ) : miniprogrameQrCodeUrl ? (
                      <img alt="QR" style={{ height: '100%', width: '100%' }} src={`data:image/png;base64,${miniprogrameQrCodeUrl}`}/>
                    ) : (
                      <div style={{ display: 'grid', placeItems: 'center', height: '100%', fontSize: 12, color: C.ink3 }}>{t('Failed to load QR code')}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', lineHeight: 1.6 }}>
                    扫码后，客户会被引导至 <b style={{ color: C.brand }}>图派</b> 小程序，输入密码 <b style={{ color: '#fff' }}>{password}</b> 即可开始选片。
                  </div>
                </div>

                <button onClick={() => {
                  if (copyImageToClipboard(miniprogrameQrCodeUrl)) {
                    setCopied('qr');
                    snackbarStore.showSnackbar(t('Copy image to clipboard'));
                    setTimeout(() => setCopied(null), 1500);
                  } else {
                    snackbarStore.showSnackbar(t('Failed to copy image to clipboard'), 'error');
                  }
                }} style={{
                  padding: '10px 18px', border: '1px solid rgba(255,255,255,.2)',
                  background: 'rgba(255,255,255,.06)', color: '#fff',
                  borderRadius: 6, fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}>
                  {copied === 'qr' ? '✓ 已复制' : '⧉ ' + t('Copy image to clipboard')}
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 20, background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12 }}>
            <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.brand, fontWeight: 600, marginBottom: 8 }}>
              Share settings · 分享设置
            </div>
            <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.6, marginBottom: 14 }}>
              设置从现在开始还能分享多少天。缩短或增加有效期都不会影响已登录客户；修改提取码会让旧提取码和客户会话立即失效。
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: expiryError ? 5 : 0 }}>
              <input
                data-testid="share-expiry-days-input"
                type="number"
                min="1"
                max="3650"
                step="1"
                value={expire}
                disabled={savingExpiry}
                onChange={event => {
                  setExpire(event.target.value);
                  setExpiryError('');
                }}
                aria-label="从现在起分享有效天数"
                style={{
                  width: 88, padding: '8px 10px', background: '#fff',
                  border: `1.5px solid ${expiryError ? '#e88c7c' : C.hair2}`, borderRadius: 7,
                  fontFamily: C.mono, fontSize: 14, color: C.ink, outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: C.ink2 }}>天</span>
              {[7, 14, 30].map(days => (
                <button key={days} type="button" data-testid={`share-expiry-preset-${days}`} disabled={savingExpiry} onClick={() => {
                  setExpire(days);
                  setExpiryError('');
                }} style={{
                  padding: '8px 13px', borderRadius: 7, border: `1px solid ${C.hair2}`,
                  background: Number(expire) === days ? C.paper2 : '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13,
                  cursor: savingExpiry ? 'wait' : 'pointer',
                }}>{days} 天</button>
              ))}
              <button type="button" data-testid="share-save-expiry" disabled={savingExpiry} onClick={setShareExpiry} style={{
                padding: '8px 13px', borderRadius: 7, border: 'none',
                background: C.brand, color: '#fff', fontFamily: C.sans, fontSize: 13,
                cursor: savingExpiry ? 'wait' : 'pointer',
              }}>{savingExpiry ? '保存中…' : '保存有效期'}</button>
            </div>
            {expiryError && <div data-testid="share-expiry-error" style={{ fontSize: 12, color: '#c2412e' }}>{expiryError}</div>}

            <div data-testid="share-password-editor" style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.hair}` }}>
              <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 8 }}>提取码</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <input
                    data-testid="share-new-password"
                    value={newPassword}
                    maxLength={4}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label="分享提取码"
                    onChange={event => {
                      const value = event.target.value.replace(/[^A-Za-z0-9]/g, '');
                      setNewPassword(value);
                      setNewPasswordError(value.length === 4 ? '' : '提取码需为 4 位数字或字母');
                    }}
                    style={{
                      width: 150, padding: '9px 12px', borderRadius: 7, outline: 'none',
                      border: `1.5px solid ${newPasswordError ? '#e88c7c' : C.hair2}`,
                      fontFamily: C.mono, fontSize: 16, letterSpacing: 4, textAlign: 'center',
                    }}
                  />
                  {newPasswordError && <div style={{ marginTop: 5, fontSize: 11.5, color: '#c2412e' }}>{newPasswordError}</div>}
                </div>
                <button type="button" data-testid="share-randomize-password" disabled={changingPassword} onClick={randomizePassword} style={{
                  padding: '9px 14px', borderRadius: 7, border: `1px solid ${C.hair2}`,
                  background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13,
                  cursor: changingPassword ? 'wait' : 'pointer',
                }}>随机一个</button>
                <button type="button" data-testid="share-save-password" disabled={changingPassword || newPassword === password || Boolean(validatePassword(newPassword))} onClick={changePassword} style={{
                  padding: '9px 14px', borderRadius: 7, border: 'none',
                  background: newPassword === password ? C.hair2 : C.ink,
                  color: '#fff', fontFamily: C.sans, fontSize: 13,
                  cursor: changingPassword || newPassword === password ? 'not-allowed' : 'pointer',
                }}>{changingPassword ? '保存中…' : '保存提取码'}</button>
              </div>
              <div style={{ marginTop: 9, fontSize: 12, color: C.ink3, lineHeight: 1.5 }}>
                修改后客户需要使用新提取码重新进入，已经提交的选片不会被删除。
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <button type="button" data-testid="share-cancel" onClick={cancelShare} disabled={hasSelections} style={{
              fontFamily: C.sans, fontSize: 13, fontWeight: 500,
              padding: '9px 14px', borderRadius: 7,
              background: hasSelections ? C.paper2 : '#fff', color: hasSelections ? C.ink3 : C.ink,
              border: `1px solid ${C.hair2}`, cursor: hasSelections ? 'not-allowed' : 'pointer',
            }}>{t('Cancel Share')}</button>
            {hasSelections && (
              <div data-testid="share-cancel-locked" style={{ marginTop: 8, fontSize: 12.5, color: C.ink3, lineHeight: 1.5 }}>
                已有客户提交选片，无法取消分享。
              </div>
            )}
          </div>
        </div>
      )}
    </StepShell>
  );
}));

export default withAuth(Step4);
